/**
 * Application-wide providers, composed in one place so `main.tsx` and tests
 * mount the exact same tree. Must be rendered inside a router — the 401 bridge
 * navigates.
 */
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { TierLimitBoundary } from '../components/tier-limit-cta';
import { createLayoutRepository, setLayoutRepository } from '../layout-platform/persistence';
import {
  createApiClient,
  resolveApiBaseUrl,
  type ApiClient,
  type AuthContextProvider,
} from '../lib/api-client';
import { APP_EVENTS, ROUTES } from '../lib/constants';

import { ActiveEventProvider } from './active-event-context';
import { PreferencesProvider } from './preferences-context';
import { SessionProvider, useSession } from './session-context';
import { ToastProvider, useToast } from './toast-context';

const ApiClientContext = createContext<ApiClient | null>(null);

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) throw new ApiClientContextError();
  return client;
}

/** The API client when mounted inside `AppProviders`, else null (shared components in isolation tests). */
export function useOptionalApiClient(): ApiClient | null {
  return useContext(ApiClientContext);
}

export function AppProviders({
  children,
  apiClient: injectedClient,
}: {
  children: ReactNode;
  /** Tests inject a stub; production builds get the real HTTP client. */
  apiClient?: ApiClient;
}): JSX.Element {
  // The client reads auth context at call time through this holder, which the
  // session bridge below keeps in sync — this breaks the provider/client cycle.
  const authRef = useRef<AuthContextProvider>({ accessToken: null, tenantId: null });

  const apiClient = useMemo<ApiClient>(
    () =>
      injectedClient ??
      createApiClient({
        baseUrl: resolveApiBaseUrl(),
        getAuthContext: () => authRef.current,
        onUnauthorized: () => {
          globalThis.dispatchEvent?.(new CustomEvent(APP_EVENTS.unauthorized));
        },
        // 402 TIER_LIMIT_EXCEEDED from anywhere in the app (FR-BILLING-05):
        // re-broadcast so <TierLimitBoundary> can show the upgrade CTA with
        // the service's own limit/usage numbers.
        onTierLimit: (error) => {
          globalThis.dispatchEvent?.(
            new CustomEvent(APP_EVENTS.tierLimitExceeded, { detail: error })
          );
        },
      }),
    [injectedClient]
  );

  // The board editors (Play Entry, Coaches Highlight) persist through the API;
  // their repository resolves layouts synchronously from a cache this client
  // hydrates. Installed during render (not in an effect) so the first board
  // paint under this provider already reads through the API-backed instance.
  useMemo(() => setLayoutRepository(createLayoutRepository(apiClient)), [apiClient]);

  return (
    <ApiClientContext.Provider value={apiClient}>
      <ToastProvider>
        <SessionProvider apiClient={apiClient}>
          <SessionBridge authRef={authRef}>{children}</SessionBridge>
        </SessionProvider>
      </ToastProvider>
    </ApiClientContext.Provider>
  );
}

/**
 * Glue between the session and everything that is not a React consumer:
 * keeps the API client's auth headers current and turns a 401 into a
 * sign-out + redirect.
 */
function SessionBridge({
  authRef,
  children,
}: {
  authRef: React.MutableRefObject<AuthContextProvider>;
  children: ReactNode;
}): JSX.Element {
  const { session, signOut, activeTenant } = useSession();
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const navigate = useNavigate();

  authRef.current = {
    accessToken: session?.accessToken ?? null,
    // Only the fanatic persona needs an explicit tenant header; for everyone
    // else the gateway derives the tenant from the token's `tid` claim. A
    // Fanatic token has no `tid` at all, so the header comes from the team the
    // portal's switcher has selected (falling back to the stored session).
    tenantId: session?.persona === 'fanatic' ? (activeTenant?.tenantId ?? session.tenantId) : null,
  };

  useEffect(() => {
    const onUnauthorized = (): void => {
      void signOut({ isRemote: false });
      showToast('Your session expired. Please sign in again.', 'error');
      navigate(ROUTES.login, { replace: true });
    };
    globalThis.addEventListener?.(APP_EVENTS.unauthorized, onUnauthorized);
    return () => globalThis.removeEventListener?.(APP_EVENTS.unauthorized, onUnauthorized);
  }, [navigate, showToast, signOut]);

  return (
    <PreferencesProvider>
      <ActiveEventProvider apiClient={apiClient}>
        {children}
        {/* Mounted once, above every screen: any 402 surfaces the upgrade CTA. */}
        <TierLimitBoundary />
      </ActiveEventProvider>
    </PreferencesProvider>
  );
}

class ApiClientContextError extends Error {
  constructor() {
    super('useApiClient() must be called inside <AppProviders>.');
    this.name = 'ApiClientContextError';
  }
}
